import AddIcon from '@mui/icons-material/Add';
import EventRepeat from '@mui/icons-material/EventRepeat';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import Timeline from '@mui/lab/Timeline';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  Paper,
  Stack,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import HorseDueDates from '../../../../../components/HorseDueDates';
import NoteItem from '../../../../../components/NoteItem';
import medicalIcon from '../../../../../images/unused-undraw-illustrations/medical.svg';
import {
  getHorseNoteHighlights,
  getHorseNotes,
} from '../../../../../redux/horse/actions';

const HealthSectionComponent = ({
  id,
  isAdmin,
  openAddEditNoteModal,
  condensed,
}) => {
  const dispatch = useDispatch();

  // Redux state
  const horse = useSelector((state) => state.horse.horse);
  const horseNotes = useSelector((state) => state.horse.horseNotes);
  const isFetchingHorseNotes = useSelector((state) => state.horse.isFetchingHorseNotes);
  const currentHorseLodging = useSelector((state) => state.horse.currentHorseLodging);

  // State
  const [pageSize] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [sortModel] = React.useState([
    {
      field: 'createdTimestamp',
      sort: 'desc',
    },
  ]);
  const [noteTypeFilter] = React.useState('Medical');

  React.useEffect(() => {
    dispatch(getHorseNotes(id, (page - 1), pageSize, sortModel.map((s) => `&sort=${s.field},${s.sort}`), noteTypeFilter || null, null));
    dispatch(getHorseNoteHighlights(id));
  }, [id, page, noteTypeFilter]);

  const closeAddEditModalCallbcak = () => {
    // reload
    dispatch(getHorseNotes(id, (page - 1), pageSize, sortModel.map((s) => `&sort=${s.field},${s.sort}`), noteTypeFilter || null, null));
  };

  return (
    <Grid container spacing={2}>
      <Grid
        size={{
          xs: 12,
          md: condensed
            ? 12
            : 6,
          lg: condensed
            ? 12
            : 8,
        }}
      >
        <Paper
          sx={{
            borderRadius: 3,
            p: 2,
          }}
        >
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            spacing={2}
            alignItems={{
              xs: 'flex-start',
              sm: 'center',
            }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar><MedicalServicesIcon /></Avatar>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Health
              </Typography>
            </Stack>
            <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={() => openAddEditNoteModal(null, noteTypeFilter, closeAddEditModalCallbcak)}>Add Health Note</Button>
          </Stack>
          <Box>
            <Timeline>
              {
                horseNotes.content.map((note) => (
                  <NoteItem
                    note={note}
                    horseId={id}
                    pinnedHorseNoteId={horse.activeNote
                      ? horse.activeNote.id
                      : null}
                    key={note.id}
                    includeUserInfo
                    isLoading={isFetchingHorseNotes}
                    onEdit={() => openAddEditNoteModal(note, noteTypeFilter, closeAddEditModalCallbcak)}
                    isAdmin={isAdmin}
                  />
                ))
              }
              {
                (!isFetchingHorseNotes && horseNotes.content.length === 0) && (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="subtitle1" align="center" component="div" sx={{ m: 4 }}>
                      No Health Notes
                    </Typography>
                    <img
                      src={medicalIcon}
                      alt="doctor with clipboard"
                      style={{
                        width: 250,
                        display: 'block',
                        margin: 'auto',
                        marginBottom: '1rem',
                      }}
                    />
                  </Box>
                )
              }
            </Timeline>
            {(horseNotes.totalPages || 0) > 1 && (
              <Pagination count={horseNotes.totalPages} variant="outlined" page={page} onChange={(event, value) => { setPage(value); }} disabled={isFetchingHorseNotes} />
            )}
          </Box>
        </Paper>
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: condensed
            ? 12
            : 6,
          lg: condensed
            ? 12
            : 4,
        }}
      >
        <Card sx={{ p: 2 }}>
          <CardHeader
            avatar={<Avatar><EventRepeat color="primary" /></Avatar>}
            title="Health Tasks"
            subheader={(
              <>
                Reminders tied to tasks tagged <b>Health</b> are shown below. This horse must also be included in each reminder.
                {isAdmin && (
                  <>
                    &nbsp;You can create new Health tasks or tag existing tasks on the <a href="/settings/tasks">Task Settings page</a>.
                  </>
                )}
                {isAdmin && (
                  <>
                    &nbsp;You can manage this horse’s reminders on the <a href="/settings/tasks/reminders">Reminder Settings page</a>.
                  </>
                )}
              </>
            )}
          />
        </Card>
        <Box sx={{ mt: 2 }}>
          <HorseDueDates isAdmin barnHorse={currentHorseLodging} health />
        </Box>
      </Grid>
    </Grid>
  );
};

HealthSectionComponent.propTypes = {
  id: PropTypes.string.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  openAddEditNoteModal: PropTypes.func.isRequired,
  condensed: PropTypes.bool,
};

HealthSectionComponent.defaultProps = { condensed: false };

export default HealthSectionComponent;
